import React, { useState } from 'react';
import { post } from 'aws-amplify/api';

const ContactForm: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      await post({
        apiName: 'DigitalMemorials',
        path: '/contact',
        options: {
          body: formData,
          headers: {
            'Content-Type': 'application/json'
          }
        }
      });

      setSuccess(true);
      setFormData({ name: '', email: '', message: '' });
    } catch (err: any) {
      console.error('Error submitting form:', err);
      setError(err.message || 'Failed to send message. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <div className="w-full max-w-4xl mx-auto">
      {/* Status Messages */}
      {error && (
        <div className="mb-8 p-6 bg-[#1A1A1A] border border-red-500/20 rounded-xl">
          <div className="flex items-center gap-3">
            <svg className="w-5 h-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-red-500 font-montserrat text-sm font-medium">
              {error}
            </p>
          </div>
        </div>
      )}

      {success && (
        <div className="mb-8 p-6 bg-[#1A1A1A] border border-primary/20 rounded-xl">
          <div className="flex items-center gap-3">
            <svg className="w-5 h-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <p className="text-primary font-montserrat text-sm font-medium">
              Message sent successfully! We'll get back to you soon.
            </p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-12">
        {/* Name and Email Fields Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          <div className="relative">
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="w-full bg-transparent text-white py-2 border-0 border-b border-white/20 focus:border-primary focus:ring-0 transition-colors duration-300 font-montserrat placeholder-white/40"
              placeholder="Name"
              required
              disabled={loading}
            />
          </div>

          <div className="relative">
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full bg-transparent text-white py-2 border-0 border-b border-white/20 focus:border-primary focus:ring-0 transition-colors duration-300 font-montserrat placeholder-white/40"
              placeholder="Email"
              required
              disabled={loading}
            />
          </div>
        </div>

        {/* Message Field */}
        <div className="relative">
          <textarea
            name="message"
            value={formData.message}
            onChange={handleChange}
            className="w-full h-32 bg-transparent text-white pt-20 pb-2 px-0 border-0 border-b border-white/20 focus:border-primary focus:ring-0 transition-colors duration-300 font-montserrat resize-none placeholder-white/40"
            placeholder="Message"
            required
            disabled={loading}
          />
        </div>

        {/* Submit Button */}
        <div className="text-center pt-4">
          <button
            type="submit"
            disabled={loading}
            className={`px-8 py-4 text-lg font-semibold rounded-lg bg-gradient-to-r from-primary to-[#E6C45C] text-[#0A0A0A] transition-all duration-300 shadow-[0_0_20px_rgba(212,175,55,0.3)] font-montserrat ${
              loading 
                ? 'opacity-50 cursor-not-allowed' 
                : 'hover:opacity-90 hover:shadow-[0_0_30px_rgba(212,175,55,0.4)] hover:scale-[1.02]'
            }`}
          >
            {loading ? 'Sending...' : 'Send Message'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ContactForm; 